import React, { useMemo } from 'react';
import moment from 'moment';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { useMutation } from '@apollo/client';
import { Button } from '@gsa/afp-component-library';
import { useRecoilState, useSetRecoilState } from 'recoil';
import useSimpleAlertModal from '@/hooks/use-simple-alert-modal';
import { useEditSolicitation } from '../../components/apis/edit-solicitation-provider';
import { getOpenPeriodYupSchema } from './open-period-yup-schema';
import ClearReopenPeriodsModal from './clear-reopen-periods-modal';
import { getTimestampFromDateNTime } from '../../utils/open-reopen-period-util';
import { OpenSolicitationReadonlyEditPeriodEndDateTimeFields } from '../../utils/solicitation-period-utils';
import { SOLICITATION_STATUSES, TO_CONTINUE_FIELD } from '../../constants';
import { ADD_OR_UPDATE_SOLICITATION_PERIOD } from '../../queries';
import SolicitationPeriod from '../../components/solicitation-period/solicitation-period';
import SolicitationHeader from '../../components/solicitation-header';
import SolicitationStepper from '../../components/solicitation-stepper';
import InactiveSIAlert from '../../components/solicitation-alert/inactive-si-alert';
import SolicitationNotification from '../../utils/solicitation-notification';
import solicitationNotifications from '../../utils/solicitation-notification-helpers';
import { vendorIds } from '../../atoms/solicitation-helpers';
import { getSolicitationPeriodMinDate } from '../../utils/open-periods-helpers';
import { pageErrorNotifications } from '../../../../utilities/error-handling';

const OpenPeriod = () => {
  const navigate = useNavigate();
  const setSolicitationNotification = useSetRecoilState(
    solicitationNotifications,
  );
  const {
    isModalOpen: showClearAdditionalPeriods,
    handleToggleModal: toggleClearShowAdditionalPeriods,
    handleOnModalProceed,
  } = useSimpleAlertModal();

  const {
    solicitation,
    solicitationID,
    openPeriod,
    currentPeriod,
    contracts,
    adjacentReopenPeriodStartDate,
    refetchSolicitation,
  } = useEditSolicitation();

  const [selectedVendorIds] = useRecoilState(vendorIds);

  const idsArr = useMemo(() => {
    if (selectedVendorIds.length > 0) {
      return selectedVendorIds;
    }

    const ids =
      currentPeriod?.vendors?.map((vendor) => ({
        vendorId: vendor.vendorId,
        audienceType: vendor.audienceType,
      })) || [];

    return ids;
  }, [selectedVendorIds, currentPeriod?.vendors]);

  const isOpenPeriodOverlapReopenPeriodAllowed = [
    SOLICITATION_STATUSES.Draft,
    SOLICITATION_STATUSES.New,
  ].includes(solicitation.status);

  const newOpenPeriodMinDateFormated = getSolicitationPeriodMinDate(
    solicitation.contractYear,
  );

  const onClickPrev = () => {
    setSolicitationNotification([]);
    navigate(`/catalog/solicitations/edit/${solicitationID}`);
  };

  const navigateNextPage = () => {
    navigate(`/catalog/solicitations/edit/${solicitationID}/reopen-period`);
  };

  const onClickNext = () => {
    setSolicitationNotification([]);
    navigateNextPage();
  };

  const [addOrUpdateSolicitationPeriod] = useMutation(
    ADD_OR_UPDATE_SOLICITATION_PERIOD,
    {
      onError: (requestError) => {
        setSolicitationNotification([
          ...pageErrorNotifications(requestError, {
            id: 'ADD_OR_UPDATE_SOLICITATION_PERIOD_ERROR',
            message: requestError.message,
            type: 'error',
            closeable: false,
            showInModal: false,
          }),
        ]);
      },
    },
  );

  const readonlyFields = useMemo(() => {
    if (
      contracts.some(({ contractYear, renewalYear }) => {
        return (renewalYear || contractYear) === solicitation.contractYear;
      })
    ) {
      return OpenSolicitationReadonlyEditPeriodEndDateTimeFields;
    }

    if (
      solicitation.cronUpdatedAt ||
      solicitation.status === 'Open' ||
      solicitation.status === 'Closed'
    ) {
      if (moment(openPeriod?.endDate).isBefore(moment())) {
        return OpenSolicitationReadonlyEditPeriodEndDateTimeFields;
      }

      if (moment(openPeriod?.startDate).isBefore(moment())) {
        return ['startDate', 'startTime', 'audience', 'vendor'];
      }
    }

    return [];
  }, [contracts, openPeriod, solicitation]);

  const {
    register,
    handleSubmit,
    control,
    watch,
    trigger,
    formState: { errors },
    setValue,
  } = useForm({
    resolver: yupResolver(
      getOpenPeriodYupSchema({
        status: solicitation.status,
        maxDateTime: isOpenPeriodOverlapReopenPeriodAllowed
          ? null
          : adjacentReopenPeriodStartDate,
        readonlyFields,
      }),
    ),
    mode: 'onBlur',
    reValidateMode: 'onChange',
    defaultValues: {
      audience: currentPeriod?.audience,
      startTime: currentPeriod?.startTime || '',
      endTime: currentPeriod?.endTime || '',
      startDate: currentPeriod?.startDate || '',
      endDate: currentPeriod?.endDate || '',
      isStartDateDisabled: readonlyFields.includes('startDate'),
      isStartTimeDisabled: readonlyFields.includes('startTime'),
      isEndDateDisabled: readonlyFields.includes('endDate'),
      isEndTimeDisabled: readonlyFields.includes('endTime'),
    },
  }) || {};

  const submitHandler = (data) => {
    const goNext = data[TO_CONTINUE_FIELD];
    setSolicitationNotification([]);

    // For draft, allow all date/time fields to be empty (all or nothing).
    // In this case, audience input will be ignored.
    // Need further discussion on how to handle this case.
    const { startDate, endDate, startTime, endTime, audience } = data || {};
    if (startDate && endDate && startTime && endTime && audience) {
      const newEndDateTime = getTimestampFromDateNTime(endDate, endTime);
      if (
        !!adjacentReopenPeriodStartDate &&
        !showClearAdditionalPeriods &&
        new Date(newEndDateTime) > new Date(adjacentReopenPeriodStartDate)
      ) {
        toggleClearShowAdditionalPeriods(data);
      } else {
        // create or update open period
        void addOrUpdateSolicitationPeriod({
          variables: {
            solicitationPeriodInput: {
              ...(openPeriod.id && { id: Number(openPeriod.id) }),
              solicitationId: Number(solicitationID),
              startDate: getTimestampFromDateNTime(startDate, startTime),
              endDate: newEndDateTime,
              audience,
              vendorIds: idsArr,
            },
          },
          onCompleted: () => {
            setSolicitationNotification([
              {
                id: 'ADD_OR_UPDATE_SOLICITATION_PERIOD',
                message: 'Open period was successfully saved.',
                type: 'success',
                closeable: true,
                showInModal: false,
                showOnNextPage: goNext,
              },
            ]);
            if (goNext) navigateNextPage();
            else refetchSolicitation();
          },
        });
      }
    } else if (data[TO_CONTINUE_FIELD]) {
      navigateNextPage();
    }
  };

  return (
    <>
      <ClearReopenPeriodsModal
        isOpen={showClearAdditionalPeriods}
        onClose={toggleClearShowAdditionalPeriods}
        onProceedCallback={handleOnModalProceed(submitHandler)}
      />
      <SolicitationNotification isModal={false} />
      <InactiveSIAlert
        solicitationPrograms={solicitation.programs}
        contractYear={solicitation.contractYear}
      />
      <SolicitationHeader
        solicitationNumber={solicitation.solicitationNumber}
        solicitationStatus={solicitation.status}
        solicitationType={solicitation.solicitationType}
      />
      <SolicitationStepper currentStep={2} solicitation={solicitation} />
      <form
        id="form"
        onSubmit={handleSubmit(submitHandler)}
        className="open-period-form"
        data-testid="open-period-form"
        noValidate
      >
        <SolicitationPeriod
          title="Enter your Open Period information. These fields are optional until you are ready to submit your solicitation."
          contractYear={solicitation.contractYear}
          currentSolicitationPeriod={currentPeriod}
          formRegister={register}
          formControl={control}
          formWatch={watch}
          formErrors={errors}
          formValidationTrigger={trigger}
          minDate={newOpenPeriodMinDateFormated}
          readonlyFields={readonlyFields}
          idPrefix="openPeriod"
          rawSolPeriod={openPeriod}
        />
        <div className="grid-row padding-top-6">
          <Button
            label="Previous"
            type="button"
            data-testid="open-period-previous-button"
            leftIcon={{
              name: 'arrow_back',
            }}
            onClick={onClickPrev}
            variant="outline"
          />
          {readonlyFields.includes('endDate') && (
            <Button
              label="Next"
              variant="outline"
              className="margin-left-1"
              data-testid="open-period-next-button"
              rightIcon={{
                name: 'arrow_forward',
              }}
              onClick={onClickNext}
            />
          )}
          {!readonlyFields.includes('endDate') && (
            <Button
              label="Save"
              variant="primary"
              className="margin-left-1"
              type="submit"
              data-testid="open-period-save-button"
              onClick={() => setValue(TO_CONTINUE_FIELD, false)}
            />
          )}
          {!readonlyFields.includes('endDate') && (
            <Button
              label="Save and continue"
              variant="primary"
              className="margin-left-1"
              type="submit"
              data-testid="open-period-save-continue-button"
              rightIcon={{
                name: 'arrow_forward',
              }}
              onClick={() => setValue(TO_CONTINUE_FIELD, true)}
            />
          )}
        </div>
      </form>
    </>
  );
};

export default OpenPeriod;
